import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const embeddingComponent: ComponentDef = {
  id: 'embedding',
  name: 'Embedding',
  description: 'Token / position / word-piece lookup table with configurable positional encoding.',
  category: 'component',
  params: [
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'vocab_size', label: 'Vocab size', type: 'number', default: 32000, min: 1000, max: 200000, step: 1000 },
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'token',
      options: [
        { label: 'Token', value: 'token' },
        { label: 'Position', value: 'position' },
        { label: 'WordPiece', value: 'wordpiece' },
        { label: 'BPE', value: 'bpe' }
      ]
    },
    {
      name: 'pos_encoding',
      label: 'Positional Encoding',
      type: 'select',
      default: 'none',
      options: [
        { label: 'None', value: 'none' },
        { label: 'RoPE', value: 'rope' },
        { label: 'ALiBi', value: 'alibi' },
        { label: 'Sinusoidal', value: 'sinusoidal' },
        { label: 'Learned', value: 'learned' }
      ]
    }
  ],
  buildGraph: (params) => {
    const seq_len = Number(params.seq_len);
    const vocab_size = Number(params.vocab_size);
    const d_model = Number(params.d_model);
    const pos_encoding = String(params.pos_encoding || 'none');

    const g = new GraphBuilder();
    g.input('ids', [seq_len]);
    g.embedding('embed', 'ids', [vocab_size, d_model]);

    if (pos_encoding === 'rope') {
      // RoPE: rotate Q/K pairs by position-dependent angles
      g.reshape('pairs', 'embed', [seq_len, d_model / 2, 2]);
      g.activation('angles', 'pairs', 'theta = pos * 10000^(-2i/d)');
      g.activation('cos_sin', 'angles', 'cos/sin');
      g.mul('rotated', 'pairs', 'cos_sin');
      g.reshape('out', 'rotated', [seq_len, d_model]);
      g.output('out', 'out');

    } else if (pos_encoding === 'alibi') {
      // ALiBi: compute position-dependent bias for attention scores
      g.activation('slopes', 'embed', 'head slopes m_h = 2^(-8h/H)');
      g.reshape('pos_diff', 'slopes', [seq_len, seq_len]);
      g.mul('alibi_bias', 'slopes', 'pos_diff');
      g.add('out', 'embed', 'alibi_bias');
      g.output('out', 'out');

    } else if (pos_encoding === 'sinusoidal') {
      // Sinusoidal: fixed sin/cos positional encoding table added to embeddings
      g.activation('pe_table', 'embed', 'PE[pos,i] = sin/cos(pos * omega_i)');
      g.add('out', 'embed', 'pe_table');
      g.output('out', 'out');

    } else if (pos_encoding === 'learned') {
      // Learned: trainable position embedding table
      g.input('pos_ids', [seq_len]);
      g.embedding('pos_embed', 'pos_ids', [seq_len, d_model]);
      g.add('out', 'embed', 'pos_embed');
      g.output('out', 'out');

    } else {
      // None: just output the raw embedding
      g.output('out', 'embed');
    }

    return g.build();
  }
};
